<template>
  <section class="w-full px-4 md:px-8 py-8 bg-[#f7f8fa] min-h-screen">
    <div class="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-3">
      <div>
        <h1 class="text-3xl font-bold text-[#152a34]">Painel Interno de KPIs</h1>
        <p class="text-sm text-gray-600 mt-1">Monitoramento de oferta, demanda e ativacao da plataforma</p>
        <router-link
          to="/google-ads-interno"
          class="inline-block mt-2 text-sm text-[#b99a52] hover:text-[#a08840] font-medium"
        >
          Abrir painel Google Ads (interno)
        </router-link>
      </div>
      <div class="flex flex-col sm:flex-row sm:items-center gap-3">
        <div class="bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
          <p class="text-xs text-gray-500">Gerado em</p>
          <p class="text-sm font-medium text-[#152a34]">{{ generatedAt || '-' }}</p>
        </div>
        <button
          type="button"
          @click="loadKpis"
          class="bg-[#152a34] hover:bg-[#1d3b49] text-white px-5 py-2.5 rounded-lg transition w-full sm:w-auto"
        >
          Atualizar
        </button>
      </div>
    </div>

    <p v-if="loading" class="text-gray-600">Carregando KPIs...</p>
    <p v-if="error" class="text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-4">
      {{ error }}
    </p>

    <div v-if="overview" class="space-y-10 mb-8">
      <!-- Plataforma -->
      <section>
        <h2 class="text-xl font-bold text-[#152a34] mb-1">Plataforma</h2>
        <p class="text-sm text-gray-600 mb-4">Usuários, prestadores e leads</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          <article class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p class="text-sm text-gray-500">Usuários totais</p>
            <p class="text-3xl font-semibold text-[#152a34]">{{ overview.usuarios_total || 0 }}</p>
          </article>
          <article class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p class="text-sm text-gray-500">Usuários ativados</p>
            <p class="text-3xl font-semibold text-green-700">{{ overview.usuarios_ativados || 0 }}</p>
          </article>
          <article class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p class="text-sm text-gray-500">Pendentes de ativação</p>
            <p class="text-3xl font-semibold text-amber-700">{{ overview.usuarios_pendentes_ativacao || 0 }}</p>
          </article>
          <article class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p class="text-sm text-gray-500">Prestadores prontos</p>
            <p class="text-3xl font-semibold text-[#152a34]">{{ overview.prestadores_prontos || 0 }}</p>
          </article>
          <article class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p class="text-sm text-gray-500">Taxa prestadores prontos</p>
            <p class="text-3xl font-semibold text-blue-700">{{ overview.taxa_prestadores_prontos_percent || 0 }}%</p>
          </article>
          <article class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p class="text-sm text-gray-500">Sem agenda</p>
            <p class="text-3xl font-semibold text-red-700">{{ overview.prestadores_sem_agenda || 0 }}</p>
          </article>
          <article class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p class="text-sm text-gray-500">Sem serviço</p>
            <p class="text-3xl font-semibold text-red-700">{{ overview.prestadores_sem_servico || 0 }}</p>
          </article>
          <article class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p class="text-sm text-gray-500">Leads totais</p>
            <p class="text-3xl font-semibold text-[#152a34]">{{ overview.leads_total || 0 }}</p>
          </article>
          <article class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p class="text-sm text-gray-500">Leads presenciais</p>
            <p class="text-3xl font-semibold text-[#152a34]">{{ overview.leads_presenciais || 0 }}</p>
          </article>
          <article class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
            <p class="text-sm text-gray-500">Leads remotos</p>
            <p class="text-3xl font-semibold text-[#152a34]">{{ overview.leads_remotos || 0 }}</p>
          </article>
        </div>
      </section>

      <!-- Appointments -->
      <section class="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h2 class="text-xl font-bold text-[#152a34] mb-1">Appointments</h2>
        <p class="text-sm text-gray-600 mb-4">Serviços presenciais agendados na plataforma</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Total</p>
            <p class="text-3xl font-semibold text-[#152a34]">{{ overview.appointments_total || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Pendentes</p>
            <p class="text-3xl font-semibold text-amber-700">{{ overview.appointments_pendentes || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Confirmados</p>
            <p class="text-3xl font-semibold text-blue-700">{{ overview.appointments_confirmados || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Em andamento</p>
            <p class="text-3xl font-semibold text-indigo-700">{{ overview.appointments_em_andamento || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Aguardando pagamento</p>
            <p class="text-3xl font-semibold text-purple-700">{{ overview.appointments_aguardando_pagamento || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Concluídos</p>
            <p class="text-3xl font-semibold text-green-700">{{ overview.appointments_concluidos || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Cancelados</p>
            <p class="text-3xl font-semibold text-red-700">{{ overview.appointments_cancelados || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Outros status</p>
            <p class="text-3xl font-semibold text-gray-700">{{ overview.appointments_outros_status || 0 }}</p>
          </article>
        </div>
        <div class="border-t border-gray-200 pt-4">
          <h3 class="font-semibold mb-3">Por status</h3>
          <ul class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            <li
              v-for="row in normalizedAppointmentsByStatus"
              :key="row.status"
              class="flex justify-between border-b border-gray-100 pb-1"
            >
              <span>{{ formatAppointmentStatus(row.status) }}</span>
              <strong>{{ row.total }}</strong>
            </li>
          </ul>
        </div>
      </section>

      <!-- Projetos -->
      <section class="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <h2 class="text-xl font-bold text-[#152a34] mb-1">Projetos</h2>
        <p class="text-sm text-gray-600 mb-4">Projetos remotos / freelancing na plataforma</p>
        <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Total</p>
            <p class="text-3xl font-semibold text-[#152a34]">{{ overview.projetos_reais_total || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Abertos</p>
            <p class="text-3xl font-semibold text-blue-700">{{ overview.projetos_reais_abertos || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Em andamento</p>
            <p class="text-3xl font-semibold text-yellow-700">{{ overview.projetos_reais_em_andamento || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Aguardando aprovação</p>
            <p class="text-3xl font-semibold text-orange-700">{{ overview.projetos_reais_aguardando_aprovacao || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Aguardando pagamento</p>
            <p class="text-3xl font-semibold text-purple-700">{{ overview.projetos_reais_aguardando_pagamento || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Pagamento aprovado</p>
            <p class="text-3xl font-semibold text-emerald-700">{{ overview.projetos_reais_pagamento_aprovado || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Concluídos</p>
            <p class="text-3xl font-semibold text-green-700">{{ overview.projetos_reais_concluidos || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Cancelamento solicitado</p>
            <p class="text-3xl font-semibold text-amber-700">{{ overview.projetos_reais_cancelamento_solicitado || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Cancelados</p>
            <p class="text-3xl font-semibold text-red-700">{{ overview.projetos_reais_cancelados || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Disputa aberta</p>
            <p class="text-3xl font-semibold text-fuchsia-700">{{ overview.projetos_reais_disputa_aberta || 0 }}</p>
          </article>
          <article class="bg-[#f7f8fa] rounded-lg border border-gray-200 p-4">
            <p class="text-sm text-gray-500">Outros status</p>
            <p class="text-3xl font-semibold text-gray-700">{{ overview.projetos_reais_outros_status || 0 }}</p>
          </article>
        </div>
        <div class="border-t border-gray-200 pt-4">
          <h3 class="font-semibold mb-3">Por status</h3>
          <ul class="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
            <li
              v-for="row in normalizedProjectsByStatus"
              :key="row.status"
              class="flex justify-between border-b border-gray-100 pb-1"
            >
              <span>{{ formatProjectStatus(row.status) }}</span>
              <strong>{{ row.total }}</strong>
            </li>
          </ul>
        </div>
      </section>
    </div>

    <div class="grid grid-cols-1 xl:grid-cols-2 gap-6">
      <section class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm xl:col-span-2">
        <h2 class="font-semibold mb-3">Listagens de leads por tipo</h2>
        <div class="grid grid-cols-1 xl:grid-cols-2 gap-4">
          <div class="border border-gray-200 rounded-lg p-3">
            <h3 class="font-medium text-[#152a34] mb-2">Leads presential (top 100)</h3>
            <div class="overflow-auto max-h-80">
              <table class="w-full text-sm">
                <thead>
                  <tr class="text-left border-b">
                    <th class="py-2 pr-2">ID</th>
                    <th class="py-2 pr-2">Usuário</th>
                    <th class="py-2 pr-2">Email</th>
                    <th class="py-2 pr-2">Address</th>
                    <th class="py-2">Data</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in leadsPresentialList" :key="`lp-${row.id}`" class="border-b">
                    <td class="py-2 pr-2">{{ row.id }}</td>
                    <td class="py-2 pr-2">{{ row.user_id || '-' }}</td>
                    <td class="py-2 pr-2">{{ row.email || '-' }}</td>
                    <td class="py-2 pr-2">{{ row.address || '-' }}</td>
                    <td class="py-2">{{ formatDateTime(row.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <div class="border border-gray-200 rounded-lg p-3">
            <h3 class="font-medium text-[#152a34] mb-2">Leads remote (top 100)</h3>
            <div class="overflow-auto max-h-80">
              <table class="w-full text-sm">
                <thead>
                  <tr class="text-left border-b">
                    <th class="py-2 pr-2">ID</th>
                    <th class="py-2 pr-2">Usuário</th>
                    <th class="py-2 pr-2">Email</th>
                    <th class="py-2">Data</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="row in leadsRemoteList" :key="`lr-${row.id}`" class="border-b">
                    <td class="py-2 pr-2">{{ row.id }}</td>
                    <td class="py-2 pr-2">{{ row.user_id || '-' }}</td>
                    <td class="py-2 pr-2">{{ row.email || '-' }}</td>
                    <td class="py-2">{{ formatDateTime(row.created_at) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <section class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm xl:col-span-2">
        <h2 class="font-semibold mb-3">Usuários novos por dia (30 dias) - ativados x pendentes</h2>
        <ul class="space-y-2 max-h-80 overflow-auto">
          <li v-for="row in usersByDay" :key="row.data" class="flex justify-between border-b pb-1">
            <span class="w-36">{{ formatDate(row.data) }}</span>
            <span class="text-green-700">Ativados: <strong>{{ row.activated || 0 }}</strong></span>
            <span class="text-amber-700">Pendentes: <strong>{{ row.pending || 0 }}</strong></span>
            <span class="text-gray-500">Cancelados: <strong>{{ row.canceled || 0 }}</strong></span>
            <span>Total: <strong>{{ row.total || 0 }}</strong></span>
          </li>
        </ul>
      </section>
    </div>

    <div class="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
      <section class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h2 class="font-semibold mb-3">Prestadores por categoria (presencial)</h2>
        <input
          v-model.trim="providersByCategorySearch"
          type="text"
          placeholder="Buscar categoria ou tipo..."
          class="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#152a34]/20"
        />
        <div class="overflow-auto max-h-[520px]">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left border-b">
                <th class="py-2 pr-2">Categoria</th>
                <th class="py-2 pr-2">Tipo</th>
                <th class="py-2 text-right">Prestadores</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredProvidersByCategory" :key="`${row.category_id}-${row.category_name}`" class="border-b">
                <td class="py-2 pr-2">{{ row.category_name || 'Sem categoria' }}</td>
                <td class="py-2 pr-2">{{ row.category_type || '-' }}</td>
                <td class="py-2 text-right font-semibold">{{ row.providers_count || 0 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h2 class="font-semibold mb-3">Prestadores por categoria e cidade (presencial)</h2>
        <input
          v-model.trim="providersByCategoryCitySearch"
          type="text"
          placeholder="Buscar categoria ou cidade..."
          class="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#152a34]/20"
        />
        <div class="overflow-auto max-h-[520px]">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left border-b">
                <th class="py-2 pr-2">Categoria</th>
                <th class="py-2 pr-2">Cidade</th>
                <th class="py-2 text-right">Prestadores</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredProvidersByCategoryCity" :key="`${row.category_id}-${row.city}`" class="border-b">
                <td class="py-2 pr-2">{{ row.category_name || 'Sem categoria' }}</td>
                <td class="py-2 pr-2">{{ row.city || 'Cidade não informada' }}</td>
                <td class="py-2 text-right font-semibold">{{ row.providers_count || 0 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h2 class="font-semibold mb-3">Prestadores por cidade (presencial)</h2>
        <input
          v-model.trim="providersByCitySearch"
          type="text"
          placeholder="Buscar cidade..."
          class="w-full mb-3 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#152a34]/20"
        />
        <div class="overflow-auto max-h-[520px]">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left border-b">
                <th class="py-2 pr-2">Cidade</th>
                <th class="py-2 text-right">Prestadores</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in filteredProvidersByCity" :key="row.city" class="border-b">
                <td class="py-2 pr-2">{{ row.city || 'Cidade não informada' }}</td>
                <td class="py-2 text-right font-semibold">{{ row.providers_count || 0 }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div class="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
      <section class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h2 class="font-semibold mb-3">Prestadores sem agenda (top 100)</h2>
        <div class="overflow-auto max-h-[420px]">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left border-b">
                <th class="py-2 pr-2">ID</th>
                <th class="py-2 pr-2">Nome</th>
                <th class="py-2 pr-2">Email</th>
                <th class="py-2">Tipo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in providersWithoutAvailability" :key="`no-av-${row.id}`" class="border-b">
                <td class="py-2 pr-2">{{ row.id }}</td>
                <td class="py-2 pr-2">{{ row.name }}</td>
                <td class="py-2 pr-2">{{ row.email }}</td>
                <td class="py-2">{{ row.provider_type }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="bg-white rounded-xl border border-gray-200 p-5 shadow-sm">
        <h2 class="font-semibold mb-3">Prestadores sem serviço (top 100)</h2>
        <div class="overflow-auto max-h-[420px]">
          <table class="w-full text-sm">
            <thead>
              <tr class="text-left border-b">
                <th class="py-2 pr-2">ID</th>
                <th class="py-2 pr-2">Nome</th>
                <th class="py-2 pr-2">Email</th>
                <th class="py-2">Tipo</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in providersWithoutServices" :key="`no-svc-${row.id}`" class="border-b">
                <td class="py-2 pr-2">{{ row.id }}</td>
                <td class="py-2 pr-2">{{ row.name }}</td>
                <td class="py-2 pr-2">{{ row.email }}</td>
                <td class="py-2">{{ row.provider_type }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>
  </section>
</template>

<script>
import { getInternalKpis } from '@/services/api';

export default {
  name: 'InternalKpiDashboard',
  data() {
    return {
      loading: false,
      error: '',
      overview: null,
      generatedAt: '',
      appointmentsByStatus: [],
      projectsByStatus: [],
      leadsPresentialList: [],
      leadsRemoteList: [],
      usersByDay: [],
      providersByCategory: [],
      providersByCategoryCity: [],
      providersByCity: [],
      providersByCategorySearch: '',
      providersByCategoryCitySearch: '',
      providersByCitySearch: '',
      providersWithoutAvailability: [],
      providersWithoutServices: [],
    };
  },
  computed: {
    normalizedAppointmentsByStatus() {
      const statusOrder = [
        'pending',
        'confirmed',
        'in_progress',
        'awaiting_payment',
        'completed',
        'cancelled',
      ];

      const rows = Array.isArray(this.appointmentsByStatus) ? this.appointmentsByStatus : [];
      const map = new Map(rows.map((row) => [String(row.status || ''), Number(row.total || 0)]));

      return statusOrder.map((status) => ({
        status,
        total: map.get(status) || 0,
      }));
    },
    normalizedProjectsByStatus() {
      const statusOrder = [
        'open',
        'in_progress',
        'awaiting_approval',
        'awaiting_payment',
        'payment_approved',
        'completed',
        'cancel_requested',
        'cancelled',
        'dispute_opened',
      ];

      const rows = Array.isArray(this.projectsByStatus) ? this.projectsByStatus : [];
      const map = new Map(rows.map((row) => [String(row.status || ''), Number(row.total || 0)]));

      return statusOrder.map((status) => ({
        status,
        total: map.get(status) || 0,
      }));
    },
    filteredProvidersByCategory() {
      const term = String(this.providersByCategorySearch || '').toLowerCase().trim();
      if (!term) return this.providersByCategory;
      return this.providersByCategory.filter((row) => {
        const categoryName = String(row.category_name || '').toLowerCase();
        const categoryType = String(row.category_type || '').toLowerCase();
        return categoryName.includes(term) || categoryType.includes(term);
      });
    },
    filteredProvidersByCategoryCity() {
      const term = String(this.providersByCategoryCitySearch || '').toLowerCase().trim();
      if (!term) return this.providersByCategoryCity;
      return this.providersByCategoryCity.filter((row) => {
        const categoryName = String(row.category_name || '').toLowerCase();
        const city = String(row.city || '').toLowerCase();
        return categoryName.includes(term) || city.includes(term);
      });
    },
    filteredProvidersByCity() {
      const term = String(this.providersByCitySearch || '').toLowerCase().trim();
      if (!term) return this.providersByCity;
      return this.providersByCity.filter((row) => {
        const city = String(row.city || '').toLowerCase();
        return city.includes(term);
      });
    },
  },
  methods: {
    formatAppointmentStatus(status) {
      const labels = {
        pending: 'Pendente',
        confirmed: 'Confirmado',
        in_progress: 'Em andamento',
        awaiting_payment: 'Aguardando pagamento',
        completed: 'Concluido',
        cancelled: 'Cancelado',
      };
      return labels[status] || status || '-';
    },
    formatProjectStatus(status) {
      const labels = {
        open: 'Aberto',
        in_progress: 'Em andamento',
        awaiting_approval: 'Aguardando aprovação',
        awaiting_payment: 'Aguardando pagamento',
        payment_approved: 'Pagamento aprovado',
        completed: 'Concluído',
        cancel_requested: 'Cancelamento solicitado',
        cancelled: 'Cancelado',
        dispute_opened: 'Disputa aberta',
      };
      return labels[status] || status || '-';
    },
    formatDate(value) {
      if (!value) return '-';
      const s = String(value).trim();
      // DATE do MySQL vem como "YYYY-MM-DD"; new Date(isoDateOnly) é UTC e vira o dia anterior no BR.
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})(?:T|$)/);
      if (m) {
        const y = Number(m[1]);
        const mo = Number(m[2]) - 1;
        const d = Number(m[3]);
        return new Date(y, mo, d).toLocaleDateString('pt-BR');
      }
      return new Date(value).toLocaleDateString('pt-BR');
    },
    formatDateTime(value) {
      if (!value) return '-';
      return new Date(value).toLocaleString('pt-BR');
    },
    async loadKpis() {
      this.loading = true;
      this.error = '';
      try {
        const { data } = await getInternalKpis();
        this.overview = data?.overview || null;
        this.generatedAt = data?.generated_at ? this.formatDateTime(data.generated_at) : '';
        this.appointmentsByStatus = data?.appointments_by_status || [];
        this.projectsByStatus = data?.projects_by_status || [];
        this.leadsPresentialList = data?.leads_presential_list || [];
        this.leadsRemoteList = data?.leads_remote_list || [];
        this.usersByDay = data?.users_by_day || [];
        this.providersByCategory = data?.providers_by_category || [];
        this.providersByCategoryCity = data?.providers_by_category_city || [];
        this.providersByCity = data?.providers_by_city || [];
        this.providersWithoutAvailability = data?.providers_without_availability || [];
        this.providersWithoutServices = data?.providers_without_services || [];
      } catch (err) {
        this.error = err?.response?.data?.error || 'Falha ao carregar KPIs.';
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.loadKpis();
  },
};
</script>
